/*
 * Copyright © 2020-2024 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-06-04 10:37:19
 */
import { Dropdown, message } from 'antd'
import type { MenuProps } from 'antd'
import React, { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import {
  fetchRecommendKeywordsByInput,
  setFilter
} from '../../../actions/photo'
import CONFIG from '../../../constants/config'
import { DepartmentMemberV2 } from '../../../global'
import { setLocationRecommend } from '../../../slices/photosByLocationSlice'
import { useDebounceFn } from 'ahooks'
import { SearchOutlined, UserOutlined } from '@ant-design/icons'

interface TagsInputDropdownProps {
  tags: string[]
  input: string
  onSubmitTag: (value: string[]) => void
  onSensorOfClickEvent: (clickItem: string, restProperties: any) => void
  onSubmit: () => void
}

type RecommendWordType = {
  word: string
  rank: number
  headImage?: string
  userID?: string
  source: 'photo' | 'nickname' | 'location'
  sensorSource: string
  locationKey: string
}

type DataRecommendWordsType = {
  msg: string
  status: number
  recommendWords: RecommendWordType[]
}

const HighLight = ({ word, input }: { word: string; input: string }) => {
  const characters = word.split('').map((char, c_index) => {
    if (input.includes(char)) {
      return (
        <span key={c_index} style={{ color: '#0093FF' }}>
          {char}
        </span>
      )
    }
    return <span key={c_index}>{char}</span>
  })
  return <span>{characters}</span>
}

const createMenuItems = (
  menuData: RecommendWordType[],
  input: string
): MenuProps['items'] => {
  return menuData.map((item, index) => {
    if (item.source === 'nickname') {
      return {
        key: index,
        label: (
          <span
            onMouseDown={(e) => {
              // 阻止输入框blur
              e.preventDefault()
            }}
          >
            <UserOutlined style={{ fontSize: '16px' }} />
            <img
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '4px',
                marginRight: '8px'
              }}
              src={item.headImage + CONFIG.ossImgProcessThumb}
              alt=""
            />
            <span>
              <HighLight word={item.word} input={input} />
            </span>
          </span>
        )
      }
    }
    return {
      key: index,
      label: (
        <span
          onMouseDown={(e) => {
            // 阻止输入框blur
            e.preventDefault()
          }}
        >
          <SearchOutlined style={{ fontSize: '16px' }} />
          <HighLight word={item.word} input={input} />
        </span>
      )
    }
  })
}

// 字符串类型的放最前, 人员, 关键词
const getOrder = (item: RecommendWordType) => {
  if (item.source === 'nickname') return 1
  if (item.source === 'photo') return 2
  return 3
}

const TagsInputDropdown: React.FC<TagsInputDropdownProps> = (props) => {
  const { tags, input, onSubmitTag, onSensorOfClickEvent, onSubmit } = props
  const dispatch = useAppDispatch()
  const groupID = useAppSelector((state) => state.group.groupId)
  const filter = useAppSelector((state) => state.photo.filter)
  const [menuData, setMenuData] = React.useState<RecommendWordType[]>([])
  const currentInputRef = React.useRef<string>('')

  const sortedMenuData = useMemo(() => {
    return menuData.sort((a, b) => {
      return getOrder(a) - getOrder(b)
    })
  }, [menuData])

  const handleFetchRecommendTags = () => {
    if (!input) return
    dispatch(
      fetchRecommendKeywordsByInput({
        groupID,
        query: input
      })
    ).then((data: DataRecommendWordsType) => {
      // 检查当前input是否与发起请求时的input一致
      if (
        data.status === 0 &&
        data.recommendWords &&
        currentInputRef.current === input
      ) {
        setMenuData(data.recommendWords)
      }
    })
  }

  const { run: runFetchRecommendTags, cancel: cancelFetchRecommendTags } =
    useDebounceFn(handleFetchRecommendTags, {
      wait: 200
    })

  const onMenuClick = (e: any) => {
    const menuData = sortedMenuData[e.key]
    if (menuData.source === 'photo') {
      onSubmitTag([...tags, menuData.word])
      setMenuData([])
    } else if (menuData.source === 'location') {
      if (tags.length === 0) {
        // 只有设置第一个tag的时候，才能设置为地点关键词
        dispatch(
          setLocationRecommend({
            word: menuData.word,
            key: menuData.locationKey
          })
        )
      }
      onSubmitTag([...tags, menuData.word])
      setMenuData([])
    } else {
      // 选择人员，加入到已筛选的部门、成员中
      onSubmitTag(tags)
      setMenuData([])
      dispatch(
        setFilter({
          ...filter,
          departmentUsers: [
            {
              userID: menuData.userID,
              name: menuData.word,
              avatar: menuData.headImage,
              id: menuData.userID
            } as DepartmentMemberV2
          ]
        })
      ).then(() => {
        message.info(`已筛选"${menuData.word}的照片"`)
        onSubmit()
      })
    }
    onSensorOfClickEvent('vagueRecommendWord', {
      clickRecommendWordComment: menuData.word,
      recommendedWordPosition: e.key,
      recommendedWordSource: menuData.source,
      bool2: menuData.source === 'nickname',
      infoJson: JSON.stringify(
        sortedMenuData.map((item) => ({
          name: item.word,
          type: item.source,
          sensorSource: item.sensorSource
        }))
      )
    })
  }

  useEffect(() => {
    currentInputRef.current = input
    if (!input) {
      cancelFetchRecommendTags()
      setMenuData([])
      return
    }
    runFetchRecommendTags()
    return () => {
      cancelFetchRecommendTags()
    }
  }, [input, runFetchRecommendTags, cancelFetchRecommendTags])

  if (!menuData.length) {
    return null
  }
  return (
    <Dropdown
      menu={{
        items: createMenuItems(sortedMenuData, input),
        onClick: onMenuClick,
        style: { maxWidth: '230px' }
      }}
      open
      classNames={{ root: 'tags-input-dropdown' }}
      styles={{ root: { left: 300 } }}
      getPopupContainer={() =>
        document.getElementsByClassName('keywords-wrapper')[0] as HTMLElement
      }
    >
      <span className="tag-dropdown-bind-node"></span>
    </Dropdown>
  )
}

export default TagsInputDropdown
